import { createSlice, PayloadAction } from '@reduxjs/toolkit';

/**
 * Command slice — UI-only state for the command-chat experience.
 *
 * All data-layer concerns (sessions, messages, streaming) live in react-query
 * (`use-command-sessions`, `use-command-messages`, `use-command-agents`) plus the
 * component-local streaming state in chat-area. This slice owns only UI flags
 * that must survive across components without prop drilling.
 */
interface CommandState {
  activeAgentId: string | null;
  activeSessionId: string | null;
  sidebarOpen: boolean;
  selectedModel: string | null;
  typingTitle: { sessionId: string; title: string } | null;
}

const initialState: CommandState = {
  activeAgentId: null,
  activeSessionId: null,
  sidebarOpen: true,
  selectedModel: null,
  typingTitle: null,
};

const commandSlice = createSlice({
  name: 'command',
  initialState,
  reducers: {
    setActiveAgent(state, action: PayloadAction<string | null>) {
      const next = action.payload;
      // Re-selecting the same agent must not clear activeSessionId so URL-driven
      // session sync survives agent id re-resolution.
      if (state.activeAgentId === next) return;
      state.activeAgentId = next;
      state.activeSessionId = null;
    },
    setActiveSession(state, action: PayloadAction<string | null>) {
      state.activeSessionId = action.payload;
    },
    setSidebarOpen(state, action: PayloadAction<boolean>) {
      state.sidebarOpen = action.payload;
    },
    toggleSidebar(state) {
      state.sidebarOpen = !state.sidebarOpen;
    },
    setSelectedModel(state, action: PayloadAction<string | null>) {
      state.selectedModel = action.payload;
    },
    setTypingTitle(state, action: PayloadAction<{ sessionId: string; title: string } | null>) {
      state.typingTitle = action.payload;
    },
  },
});

export const {
  setActiveAgent,
  setActiveSession,
  setSidebarOpen,
  toggleSidebar,
  setSelectedModel,
  setTypingTitle,
} = commandSlice.actions;

export default commandSlice.reducer;
